import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getLoading,
  getData,
  getEmployees,
  getAccumulators
} from "../selectors";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import {
  getHistoricAccumulatorsAction,
  getSpreadsheetAction,
} from "../actions";
import {
  startOfMonth, endOfMonth, startOfYear, endOfYear,
  startOfQuarter, endOfQuarter,
  addMonths, subMonths, subQuarters,
  format
} from "date-fns";
import { getLocalizedString } from "@icarius-localization/strings";

const now = new Date();

const startAll = new Date(0, 0, 1);
const endAll = new Date(2200, 0, 1);

const startMonth = startOfMonth(now);
const endMonth = endOfMonth(now);

const startPrevMonth = startOfMonth(subMonths(now, 1));
const endPrevMonth = endOfMonth(subMonths(now, 1));

const startNextMonth = startOfMonth(addMonths(now, 1));
const endNextMonth = endOfMonth(addMonths(now, 1));

const startQuarter = startOfQuarter(now);
const endQuarter = endOfQuarter(now);

// Semester: months 0-5 = first semester, months 6-11 = second semester
const currentMonth = now.getMonth(); // 0-indexed
const startSemester = startOfMonth(new Date(now.getFullYear(), currentMonth + 1 > 6 ? 6 : 0, 1));
const endSemester = endOfMonth(new Date(now.getFullYear(), currentMonth + 1 > 6 ? 11 : 5, 1));

const startYear = startOfYear(now);
const endYear = endOfYear(now);

const startPrevQuarter = startOfQuarter(subQuarters(now, 1));
const endPrevQuarter = endOfQuarter(subQuarters(now, 1));

const startPrevSemester = startOfMonth(new Date(now.getFullYear(), currentMonth + 1 > 6 ? 0 : 6, 1));
const endPrevSemester = endOfMonth(new Date(now.getFullYear(), currentMonth + 1 > 6 ? 5 : 11, 1));

const startPrevYear = startOfYear(new Date(now.getFullYear() - 1, 0, 1));
const endPrevYear = endOfYear(new Date(now.getFullYear() - 1, 0, 1));

const start24Month = startOfMonth(subMonths(now, 24));
const end240Month = endOfMonth(subMonths(now, 1));

const start36Month = startOfMonth(subMonths(now, 36));
const end36Month = endOfMonth(subMonths(now, 1));


const getPeriodArray = () => {
  return [
    {
      key: -1,
      value: {
        name: getLocalizedString("showAllAlerts"),
        from: startAll,
        to: endAll,
      },
    },
    {
      key: 0,
      value: {
        name: getLocalizedString("currentMonth", { from: format(startMonth, "yyyy/MM"), to: format(endMonth, "yyyy/MM") }),
        from: startMonth,
        to: endMonth,
      },
    },
    {
      key: 1,
      value: {
        name: getLocalizedString("previousMonth", { from: format(startPrevMonth, "yyyy/MM"), to: format(endPrevMonth, "yyyy/MM") }),
        from: startPrevMonth,
        to: endPrevMonth,
      }
    },
    {
      key: 2,
      value: {
        name: getLocalizedString("nextMonth", { from: format(startNextMonth, "yyyy/MM"), to: format(endNextMonth, "yyyy/MM") }),
        from: startNextMonth,
        to: endNextMonth,
      }
    },
    {
      key: 3,
      value: {
        name: getLocalizedString("currentQuarter", { from: format(startQuarter, "yyyy/MM"), to: format(endQuarter, "yyyy/MM") }),
        from: startQuarter,
        to: endQuarter,
      }
    },
    {
      key: 4,
      value: {
        name: getLocalizedString("currentSemester", { from: format(startSemester, "yyyy/MM"), to: format(endSemester, "yyyy/MM") }),
        from: startSemester,
        to: endSemester,
      }
    },
    {
      key: 5,
      value: {
        name: getLocalizedString("currentYear", { from: format(startYear, "yyyy/MM"), to: format(endYear, "yyyy/MM") }),
        from: startYear,
        to: endYear,
      }
    },
    {
      key: 6,
      value: {
        name: getLocalizedString("previousQuarter", { from: format(startPrevQuarter, "yyyy/MM"), to: format(endPrevQuarter, "yyyy/MM") }),
        from: startPrevQuarter,
        to: endPrevQuarter,
      }
    },
    {
      key: 7,
      value: {
        name: getLocalizedString("previousSemester", { from: format(startPrevSemester, "yyyy/MM"), to: format(endPrevSemester, "yyyy/MM") }),
        from: startPrevSemester,
        to: endPrevSemester,
      }
    },
    {
      key: 8,
      value: {
        name: getLocalizedString("previousYear", { from: format(startPrevYear, "yyyy/MM"), to: format(endPrevYear, "yyyy/MM") }),
        from: startPrevYear,
        to: endPrevYear,
      }
    },
    {
      key: 9,
      value: {
        name: getLocalizedString("last24Months", { from: format(start24Month, "yyyy/MM"), to: format(end240Month, "yyyy/MM") }),
        from: start24Month,
        to: end240Month,
      }
    },
    {
      key: 10,
      value: {
        name: getLocalizedString("last36Months", { from: format(start36Month, "yyyy/MM"), to: format(end36Month, "yyyy/MM") }),
        from: start36Month,
        to: end36Month,
      }
    },
  ]
}

const useHistoricAccumulators = () => {

  const [period, setPeriod] = useState(() => getPeriodArray()[1].value);
  const [editDialogIsOpen, setEditDialogIsOpen] = useState(false);
  const [createDialogIsOpen, setCreateDialogIsOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null); //seleccionado para editar
  const [massiveMenuAnchor, setMassiveMenuAnchor] = useState(null);
  const [uploadFileDialogIsOpen, setUploadFileDialogIsOpen] = useState(false);

  const dispatch = useDispatch();
  const data = useSelector(getData);
  const isLoading = useSelector(getLoading);
  const employees = useSelector(getEmployees);
  const accumulators = useSelector(getAccumulators);

  useEffect(() => {
    dispatch(getHistoricAccumulatorsAction(period.from, period.to));
  }, [dispatch, period])

  const handleSetMassiveMenuAnchor = data => {
    setMassiveMenuAnchor(data);
  }

  const handleOpenCreateDialog = () => {
    setSelectedDocument(null);
    setCreateDialogIsOpen(true);
  }

  const handleOpenEditDialog = (gridRef) => {
    let documents = gridRef.api.getSelectedRows();
    if (documents.length !== 1) {
      dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOneRow"), severity: "warning" }));
      return;
    }

    setSelectedDocument(documents[0]);
    setEditDialogIsOpen(true);
  }

  const handleCloseCreateEditDialog = () => {
    setCreateDialogIsOpen(false);
    setEditDialogIsOpen(false);
    setSelectedDocument(null);
  }

  const handleFilterByPeriod = (newPeriod) => {
    setPeriod(newPeriod);
  }

  const handleDownloadStructure = (gridRef, emptyGrid) => {
    let rowsToInclude = [];
    if (!emptyGrid) {
      gridRef.api.forEachNodeAfterFilterAndSort(node => rowsToInclude.push(node.data));
      rowsToInclude = rowsToInclude.map(el => el.internalCode);
    }

    if (emptyGrid || rowsToInclude.length === 0) {
      dispatch(getSpreadsheetAction({ empty: true }));
    } else {
      if (rowsToInclude.length > 50000) {
        dispatch(openSnackbarAction({ msg: getLocalizedString("exportErrorHistoricAccumulators"), severity: "error" }));
      } else {
        dispatch(getSpreadsheetAction({ rowsToInclude, empty: false, from: period.from, to: period.to }));
      }
    }
    setMassiveMenuAnchor(null);
  }

  const handleOpenUploadFileDialogIsOpen = () => {
    setUploadFileDialogIsOpen(true);
  }

  const handleCloseUploadFileDialogIsOpen = () => {
    setUploadFileDialogIsOpen(false);
  }

  const handleReloadGrid = () => {
    dispatch(getHistoricAccumulatorsAction(period.from, period.to));
  }

  const state = {
    period,
    editDialogIsOpen,
    createDialogIsOpen,
    selectedDocument,
    massiveMenuAnchor,
    uploadFileDialogIsOpen,
  }

  const handlers = {
    handleOpenCreateDialog,
    handleOpenEditDialog,
    handleCloseCreateEditDialog,
    handleFilterByPeriod,
    handleDownloadStructure,
    handleSetMassiveMenuAnchor,
    handleOpenUploadFileDialogIsOpen,
    handleCloseUploadFileDialogIsOpen,
    handleReloadGrid,
  }

  return {
    data,
    employees,
    accumulators,
    periodArray: getPeriodArray(),
    isLoading,
    state,
    handlers
  }
}

export default useHistoricAccumulators;